import { RealtimeEvent } from '@taxfyle/api-internal/internal/realtime_pb'
import { StatusCode } from 'grpc-web'
import TypeCase = RealtimeEvent.TypeCase
import { filter, map, Observable } from 'rxjs'
import {
  GetJobTaxAssistantRequest,
  JobTaxAssistantUpdatedRealtimeEvent,
} from '@taxfyle/api-internal/internal/job_tax_assistant_pb'
import { isNotNil } from 'utils/object-util'
import { Id } from 'types/definitions'
import { filterNonNil } from 'utils/rx-util'
import { WorkAPI } from './API'
import {
  JobTaxAssistantDTO,
  mapJobtaxAssistantProtoToDTO,
} from './dtos/job-tax-assistant'
import JobTaxAssistant from './JobTaxAssistant'
import { collection } from '../infra/collection'
import { maybeParseDate } from 'utils/date-util'

export type JobTaxAssistantUpdatedEvent = {
  jobId: Id
  replyStatus: 'IDLE' | 'PROCESSING'
  active: boolean
}

export default class JobTaxAssistantStore {
  readonly events$: Observable<JobTaxAssistant> = this._events$.pipe(
    filter(
      (event) => event.getTypeCase() === TypeCase.JOB_TAX_ASSISTANT_UPDATED,
    ),
    map((e) => this.mapRealtimeEvent(e)),
    filterNonNil(),
  )

  /**
   * Checklist collection.
   */
  assistants = collection<JobTaxAssistantDTO, JobTaxAssistant>({
    create: () => new JobTaxAssistant(),
    idAttribute: 'job_id',
  })

  constructor(
    private api: WorkAPI,
    private _events$: Observable<RealtimeEvent>,
  ) {}

  forJob = (jobId: Id) => this.assistants.get(Id.assert(jobId))

  async fetchJobTaxAssistant(jobId: Id) {
    const request = new GetJobTaxAssistantRequest().setJobId(Id.assert(jobId))

    return this.api.jobTaxAssistants
      .get(request)
      .then((response) => mapJobtaxAssistantProtoToDTO(response))
      .then((assistant) => this.assistants.set(assistant))
      .catch((e) => {
        if (e.code === StatusCode.NOT_FOUND) {
          return undefined
        }

        throw e
      })
  }

  private mapRealtimeEvent(e: RealtimeEvent): JobTaxAssistant | undefined {
    switch (e.getTypeCase()) {
      case TypeCase.JOB_TAX_ASSISTANT_UPDATED:
        return this.mapJobTaxAssistantUpdatedRealtimeEvent(
          e.getJobTaxAssistantUpdated(),
        )
    }
  }

  private mapJobTaxAssistantUpdatedRealtimeEvent(
    event?: JobTaxAssistantUpdatedRealtimeEvent,
  ): JobTaxAssistant | undefined {
    const proto = event?.getAssistant()

    if (!isNotNil(proto)) {
      return
    }

    const assistantDTO = mapJobtaxAssistantProtoToDTO(proto)

    const assistant = this.assistants.get(assistantDTO.job_id)
    if (!assistant) {
      return this.assistants.set(assistantDTO)
    }

    const updateTime = maybeParseDate(assistantDTO.update_time)

    if (
      updateTime &&
      assistant.dateModified &&
      assistant.dateModified < updateTime
    ) {
      return assistant.set(assistantDTO, { parse: true, stripUndefined: true })
    }
  }
}
